// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { Col, Layout, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { LoginComponent } from '../../components/login/LoginComponent';
import Separator from '../../components/separator/Separator';
import './TenantLogin.less';
import {
  createClientUserSession,
  determineAuthMethod,
  lookupTenant,
} from '../../api/tenantLookupApi';
import {
  determineResource,
  getTenantSubdomain,
  getUrlParam,
  getUrlWithTenantId,
} from '../../utils';
import { v4 as uuidv4 } from 'uuid';
import { useNotifications } from '../../errorHandler/context/Notifications';
import { AppError } from '../../errorHandler/errors';
import { ErrorCode } from '../../errorHandler/constants';
import { AuthAction, type AuthResource } from '../../shared/types';
import { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Authn,
  AuthnMethod,
  type AuthnViews,
  type EventPayload,
  EventType,
} from '@imprivata-cloud/authn';
import { WEB_APP_NAME } from '../../shared/constants';
import {
  createSpanName,
  debug,
  error,
  ImprHeaders,
  SpanComponent,
} from '@imprivata-cloud/common';
import { getOrgPreferences, setDefaultHeader } from '../../api/api';
import DefaultLogofrom from '../../assets/default_logo.svg?react';
import { AuthnLayout, LayoutHeaders } from '../Card/authn-layout';

const performLookup = async (email: string, workflowId: string) => {
  const response = await lookupTenant(email, workflowId);
  // User is redirected to lookup only when tenantId is not known.
  const tenantList = response?.tenantList ?? [];
  if (tenantList.length === 0) {
    throw new AppError({ code: ErrorCode.TENANT_NOT_FOUND });
  }
  return tenantList.length === 1
    ? tenantList[0]
    : tenantList.find(t => t.tenantType === 'PRIMARY');
};

export const getTenantIdAndTargetUrl = (targetQueryParam: string | null) => {
  let tenantId = '';
  const targetUrl = targetQueryParam || '';

  if (targetQueryParam) {
    const urlObj = new URL(targetQueryParam);
    tenantId =
      urlObj.searchParams.get('tenantId') ||
      getTenantSubdomain(targetQueryParam);
  }

  return { tenantId, targetUrl };
};

const determineAuthAction = async (
  email: string | undefined,
  workflowId: string,
) => {
  const targetQueryParam = getUrlParam('target');

  let { tenantId, targetUrl } = getTenantIdAndTargetUrl(targetQueryParam);

  if (!tenantId && email) {
    const tenantIdFromLookup = await performLookup(email, workflowId);
    tenantId = tenantIdFromLookup?.tenantId || '';
    targetUrl = getUrlWithTenantId(targetQueryParam || '', tenantId);
  }

  if (!tenantId) {
    throw new AppError({ code: ErrorCode.TENANT_NOT_FOUND });
  }

  console.log('[determineAuthAction] targetUrl: ', targetUrl);

  const payload = {
    username: email,
    resource: determineResource(targetQueryParam || '') as AuthResource,
    targetUrl,
  };

  const authActionRes = await determineAuthMethod(
    payload,
    tenantId,
    workflowId,
  );

  if (
    !authActionRes ||
    (authActionRes?.action === AuthAction.START_IDP_SSO &&
      !authActionRes.startIdpSso?.startSsoUrl)
  ) {
    throw new AppError({ code: ErrorCode.TENANT_NOT_FOUND });
  }

  return { tenantId, targetUrl, ...authActionRes }; //TODO think through the return type
};

export type AuthDetails = {
  applicationId: string;
  tenantId: string;
  username?: string;
  targetUrl: string;
};

const TenantLogin = () => {
  const { t } = useTranslation();
  const { emitError } = useNotifications();
  const [actualView, setActualView] = useState<AuthnViews>();
  const [authDetails, setAuthDetails] = useState<AuthDetails>();
  const [workflowId, setWorkflowId] = useState<string>();
  const [header, setHeader] = useState<LayoutHeaders>(
    LayoutHeaders.ENROLL_CONSENT,
  );
  const [logoUrl, setLogoUrl] = useState<
    string | null | undefined | React.ReactNode
  >(undefined);

  useEffect(() => {
    const targetQueryParam = getUrlParam('target');
    const { tenantId } = getTenantIdAndTargetUrl(targetQueryParam);
    if (tenantId) {
      handleSubmit();
    }
  }, []);

  const handleSubmit = async (email?: string) => {
    try {
      const workflowId = uuidv4();
      setWorkflowId(workflowId);
      const authActionRes = await determineAuthAction(email, workflowId);

      if (authActionRes?.tenantId) {
        setDefaultHeader(ImprHeaders.ImprTenantId, authActionRes.tenantId);
        setHeader(LayoutHeaders.AUTHENTICATION);
        getLogo();
      }

      if (
        authActionRes?.action === AuthAction.START_IDP_SSO &&
        authActionRes?.startIdpSso?.startSsoUrl
      ) {
        window.location.href = authActionRes?.startIdpSso?.startSsoUrl;
      } else {
        setAuthDetails({
          tenantId: authActionRes?.tenantId,
          applicationId: authActionRes?.launchAuthnUi?.applicationId || '',
          username: email,
          targetUrl: authActionRes?.targetUrl || '',
        });
      }
    } catch (err) {
      console.error(err);
      emitError(new AppError({ code: ErrorCode.TENANT_NOT_FOUND }));
    }
  };

  const tracing = useMemo(
    () => ({
      workflowId: workflowId || '',
    }),
    [workflowId],
  );

  const requestConfig = useMemo(
    () => ({
      clientName: WEB_APP_NAME,
      username: authDetails?.username,
    }),
    [authDetails],
  );

  const deviceFactors = [
    AuthnMethod.FACE_ID,
    AuthnMethod.PASSWORD,
    AuthnMethod.IMPR_ID,
    AuthnMethod.IMPR_PIN,
    AuthnMethod.TEMP_CODE,
  ];

  const onSuccess = useCallback(async () => {
    try {
      debug('Processing authentication with code');
      console.log('[onSuccess] authDetails: ', authDetails);
      if (!authDetails?.targetUrl) {
        error(
          'Target URL is not defined',
          'Cannot redirect to an undefined target URL',
        );
        return;
      }
      //call create client session API
      await createClientUserSession(authDetails?.tenantId || '', workflowId);
      debug('Redirecting to target', { targetUrl: authDetails?.targetUrl });
      window.location.href = authDetails?.targetUrl;
    } catch (err) {
      error('Authentication failed', String(err));
      emitError(err as AppError);
      throw err;
    }
  }, [authDetails, workflowId, emitError]);

  // Handle events coming from AuthnModule
  const handleEvent = useCallback(
    ({ event, data }: EventPayload) => {
      debug('Received event', { event, data });
      createSpanName(SpanComponent.UI, 'event', `on-event--${event}`);
      switch (event) {
        case EventType.AUTHENTICATED:
          void onSuccess();
          break;
        case EventType.ERROR:
          if (data) {
            if (data.message === ErrorCode.INVALID_CREDENTIALS) {
              emitError(new AppError({ code: ErrorCode.INVALID_CREDENTIALS }));
            } else if (data.code === ErrorCode.ACCOUNT_LOCKED) {
              emitError(new AppError({ code: ErrorCode.ACCOUNT_LOCKED }));
            } else {
              emitError(new AppError({ code: ErrorCode.INTERNAL }));
            }
          }
          break;
        case EventType.VIEW_CHANGED:
          setActualView(data);
          break;
        case EventType.INTERNAL_ERROR:
          if (data) {
            if (typeof data === 'string') {
              emitError(
                new AppError({
                  message: data,
                  code: ErrorCode.UNKNOWN,
                }),
              );
            }
          }
          break;
      }
    },
    [emitError, onSuccess],
  );

  const getLogo = async () => {
    try {
      const { logoUrl } = await getOrgPreferences();

      setLogoUrl(logoUrl || <DefaultLogofrom />);
    } catch (e) {
      console.log(e);
      setLogoUrl(<DefaultLogofrom />);
    }
  };

  const { Content } = Layout;
  return (
    <Layout className={'tenant-lookup-window'}>
      <Content>
        <Row className="tenant-lookup-container">
          <Col xs={0} sm={0} md={12} lg={14} className="portal-info">
            <div className="tenant-lookup-info-steps">
              <h1>{t('tenant-lookup.info.title')}</h1>
              <Separator />
              <div>
                <p>{t('tenant-lookup.info.steps-1')}</p>
                <p>{t('tenant-lookup.info.steps-2')}</p>
              </div>
            </div>
            <TenantLookupFooter />
          </Col>
          <Col xs={24} sm={24} md={12} lg={10}>
            <div className={'login-container'}>
              <AuthnLayout
                hexImage={undefined}
                header={header}
                logoUrl={logoUrl}
              >
                {authDetails?.applicationId ? (
                  <Authn
                    tenantId={authDetails?.tenantId}
                    onEvent={handleEvent}
                    deviceFactors={deviceFactors}
                    requestConfig={requestConfig}
                    tracing={tracing}
                    hideAddAnotherMethod
                    applicationId={authDetails?.applicationId}
                  />
                ) : (
                  <LoginComponent onSubmit={handleSubmit} />
                )}
              </AuthnLayout>
            </div>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export { TenantLogin };

const TenantLookupFooter = () => {
  return (
    <div className={'tenant-lookup-info-footer'}>
      <p>© {new Date().getFullYear()} Imprivata, Inc. All rights reserved.</p>
    </div>
  );
};
